/**
 * Stage 37 · Security Readiness — evidence-only production readiness report.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";

const SEVERITY = {
  critical: "bg-rose-100 text-rose-800 border-rose-200",
  high:     "bg-orange-100 text-orange-800 border-orange-200",
  medium:   "bg-amber-100 text-amber-800 border-amber-200",
  low:      "bg-slate-100 text-slate-600 border-slate-200",
  info:     "bg-sky-100 text-sky-800 border-sky-200",
};
const GO_LIVE = {
  ready:       { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "READY" },
  conditional: { color: "bg-amber-100 text-amber-800 border-amber-200",        label: "CONDITIONAL" },
  blocked:     { color: "bg-rose-100 text-rose-800 border-rose-200",           label: "BLOCKED" },
};

export default function Readiness() {
  const [d, setD] = useState(null);
  const load = () => api.get("/hardening/readiness").then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-sm text-slate-500 p-6">Loading readiness report…</div>;
  const gl = GO_LIVE[d.go_live] || GO_LIVE.conditional;
  const s = d.findings_by_severity || {};
  return (
    <div className="max-w-5xl space-y-5" data-testid="readiness-page">
      <div className="flex items-start gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Trust · Security Readiness</div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Production Readiness Report</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Evidence-only signals across tenant isolation, RBAC, audit, integrations and environment. Never a fabricated score.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={"inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded border " + gl.color} data-testid="readiness-go-live">{gl.label}</span>
          <button onClick={load} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white inline-flex items-center gap-1"><RefreshCw className="h-4 w-4" /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Critical" value={s.critical || 0} color="text-rose-700" testid="readiness-critical" />
        <Tile label="High"     value={s.high || 0}     color="text-orange-700" />
        <Tile label="Medium"   value={s.medium || 0}   color="text-amber-700" />
        <Tile label="Low"      value={s.low || 0}      color="text-slate-700" />
        <Tile label="Info"     value={s.info || 0}     color="text-sky-700" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200 text-sm font-medium">Findings ({d.findings?.length || 0})</div>
        {(!d.findings || d.findings.length === 0) ? (
          <div className="p-6 text-sm text-emerald-800">
            <ShieldCheck className="inline h-4 w-4 mr-1" /> No findings. Every automated check passed.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {d.findings.map((f, i) => (
              <li key={i} className="p-3 flex items-start gap-3" data-testid={`readiness-finding-${i}`}>
                <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (SEVERITY[f.severity] || SEVERITY.low)}>{f.severity}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{f.area}</div>
                  <div className="text-sm text-slate-700">{f.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <details className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs" data-testid="readiness-raw">
        <summary className="cursor-pointer font-medium text-slate-700">Raw checks (as of {d.as_of?.slice(0, 16).replace("T", " ")})</summary>
        <pre className="mt-2 overflow-auto max-h-80 bg-white p-2 rounded border border-slate-100">{JSON.stringify(d.checks, null, 2)}</pre>
      </details>
    </div>
  );
}

function Tile({ label, value, color, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + (color || "text-slate-900")}>{value}</div>
    </div>
  );
}
